import {
  AnalisarResponse,
  baixarArquivo,
  FiltrosPesquisa,
  ItemDoArquivo,
  SearchResponse,
  TipoQuery,
  unidadeCanonica,
} from 'services/price_intelligence';

export const PLANILHA_EXTENSOES = ['.xlsx', '.xlsm', '.xls', '.ods', '.csv', '.tsv', '.txt'];
export const DOCUMENTO_EXTENSOES = ['.pdf', '.docx', '.odt'];
export const PLANILHA_MAX_BYTES = 5 * 1024 * 1024;
export const DOCUMENTO_MAX_BYTES = 10 * 1024 * 1024;
export const LOTE_MAX_ITENS = 300;

const BUSCA_MAX = 500;
const DESCRICAO_MAX = 1000;
const STORAGE_PREFIXO = 'pi_lote:v1:';
const BANCO = 'conlicitacao-analise-precos';
const LOJA = 'resultados-lote';

const RECUSAS: Array<{ extensoes: string[]; mensagem: string }> = [
  {
    extensoes: ['.doc', '.rtf'],
    mensagem:
      'Documento em formato antigo não é lido. Abra no Word ou no LibreOffice e salve como .docx ou PDF antes de importar.',
  },
  {
    extensoes: ['.xlsb'],
    mensagem: 'Planilha binária (.xlsb) não é lida. Salve como .xlsx antes de importar.',
  },
  {
    extensoes: ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.tif', '.tiff', '.heic'],
    mensagem:
      'Imagem não é lida: a importação precisa do texto do arquivo. Envie a planilha, o Word ou o PDF original.',
  },
  {
    extensoes: ['.zip', '.rar', '.7z'],
    mensagem: 'Arquivo compactado: envie o arquivo que está dentro dele.',
  },
];

export type StatusItemLote = 'pendente' | 'pesquisado' | 'salvo';

export interface ResumoItemLote {
  valor: number | null;
  unidade: string | null;
  amostras: number;
  refinar: string | null;
}

export interface ItemLote {
  id: string;
  numero: string | null;
  lote: string | null;
  descricao: string;
  quantidade: number | null;
  unidade: string | null;
  codigoCatalogo: string | null;
  valorReferencia: number | null;
  status: StatusItemLote;
  resumo: ResumoItemLote | null;
  pesquisaSalvaId: number | null;
}

export interface Lote {
  arquivo: string;
  importadoEm: string;
  itens: ItemLote[];
}

export type RascunhoItem = Pick<
  ItemLote,
  'numero' | 'lote' | 'descricao' | 'quantidade' | 'unidade' | 'codigoCatalogo' | 'valorReferencia'
>;

export interface ResultadoGuardado {
  resultado: SearchResponse;
  busca: {
    query: string;
    queries: string[] | null;
    specAssumida: string | null;
    tipoQuery: TipoQuery | null;
  };
  filtros: FiltrosPesquisa;
  analise: AnalisarResponse | null;
  quando: string;
}

const espacos = (texto: string) => texto.replace(/\s+/g, ' ').trim();

const terminaCom = (nome: string, extensoes: string[]) =>
  extensoes.some((ext) => nome.endsWith(ext));

const novoId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const ehDocumento = (nome: string): boolean =>
  terminaCom(nome.toLowerCase(), DOCUMENTO_EXTENSOES);

export const validarArquivo = (nome: string, tamanho: number): string | null => {
  const minusculo = nome.toLowerCase().trim();
  const documento = terminaCom(minusculo, DOCUMENTO_EXTENSOES);
  if (!documento && !terminaCom(minusculo, PLANILHA_EXTENSOES)) {
    const recusa = RECUSAS.find((r) => terminaCom(minusculo, r.extensoes));
    if (recusa) return recusa.mensagem;
    return 'Formato não suportado. Envie uma planilha (.xlsx, .xls, .ods ou .csv), um PDF ou um documento do Word (.docx) ou do LibreOffice (.odt).';
  }
  if (tamanho === 0) return 'O arquivo está vazio.';
  if (documento && tamanho > DOCUMENTO_MAX_BYTES) return 'O documento passa de 10 MB.';
  if (!documento && tamanho > PLANILHA_MAX_BYTES) return 'A planilha passa de 5 MB.';
  return null;
};

export const rascunhoDoArquivo = (item: ItemDoArquivo): RascunhoItem => ({
  numero: item.numero,
  lote: item.lote,
  descricao: espacos(item.descricao),
  quantidade: item.quantidade,
  unidade: item.unidade,
  codigoCatalogo: item.codigo_catalogo,
  valorReferencia: item.valor_referencia,
});

export const nomeDoLote = (lote: Pick<Lote, 'arquivo'>): string =>
  lote.arquivo.replace(/\.[a-z0-9]{1,5}$/i, '');

export const nomeDaPesquisaDoItem = (item: Pick<ItemLote, 'numero' | 'descricao'>): string =>
  `${item.numero ? `Item ${item.numero} — ` : ''}${item.descricao}`.slice(0, 255);

export const montarLote = (
  atual: Lote | null,
  arquivo: string,
  rascunhos: RascunhoItem[],
  modo: 'substituir' | 'adicionar',
): { lote: Lote; descartados: number } => {
  const manter = modo === 'adicionar' && atual;
  const base = manter && atual ? atual.itens : [];
  const validos = rascunhos.filter((r) => espacos(r.descricao).length > 0);
  const vagas = Math.max(0, LOTE_MAX_ITENS - base.length);
  const novos: ItemLote[] = validos.slice(0, vagas).map((r) => ({
    id: novoId(),
    numero: r.numero,
    lote: r.lote,
    descricao: espacos(r.descricao).slice(0, DESCRICAO_MAX),
    quantidade: r.quantidade,
    unidade: r.unidade ? espacos(r.unidade).toUpperCase() : null,
    codigoCatalogo: r.codigoCatalogo,
    valorReferencia: r.valorReferencia,
    status: 'pendente',
    resumo: null,
    pesquisaSalvaId: null,
  }));
  return {
    lote: {
      arquivo: manter && atual ? atual.arquivo : arquivo,
      importadoEm: manter && atual ? atual.importadoEm : new Date().toISOString(),
      itens: base.concat(novos),
    },
    descartados: validos.length - novos.length,
  };
};

export const textoDeBusca = (item: Pick<ItemLote, 'descricao'>): string => {
  const texto = espacos(item.descricao);
  if (texto.length <= BUSCA_MAX) return texto;
  const bruto = texto.slice(0, BUSCA_MAX);
  const fronteira = bruto.lastIndexOf(' ');
  return (fronteira > 0 ? bruto.slice(0, fronteira) : bruto).trim();
};

export const resumoDoResultado = (r: SearchResponse): Omit<ResumoItemLote, 'refinar'> => {
  if (r.regime === 'unitario' && r.unidade_referencia) {
    const grupo = r.unit_stats ? r.unit_stats[r.unidade_referencia] : null;
    return {
      valor: grupo && typeof grupo.mediana === 'number' ? grupo.mediana : null,
      unidade: r.unidade_referencia,
      amostras: grupo ? grupo.n : 0,
    };
  }
  if (r.regime === 'global') return { valor: null, unidade: null, amostras: r.hits.length };
  return {
    valor: r.stats && typeof r.stats.mediana === 'number' ? r.stats.mediana : null,
    unidade: null,
    amostras: r.stats ? r.stats.n : r.hits.length,
  };
};

export const totalEstimado = (
  item: Pick<ItemLote, 'quantidade' | 'unidade'> & {
    resumo: Pick<ResumoItemLote, 'valor' | 'unidade'> | null;
  },
): number | null => {
  const valor = item.resumo ? item.resumo.valor : null;
  if (valor === null || item.quantidade === null) return null;
  const unidadeDoPreco = item.resumo ? item.resumo.unidade : null;
  if (unidadeDoPreco && unidadeCanonica(item.unidade) !== unidadeCanonica(unidadeDoPreco))
    return null;
  return Math.round(valor * item.quantidade * 100) / 100;
};

export const totaisDoLote = (lote: Lote): { total: number; itensNoTotal: number } => {
  let total = 0;
  let itensNoTotal = 0;
  lote.itens.forEach((item) => {
    const t = totalEstimado(item);
    if (t !== null) {
      total += t;
      itensNoTotal += 1;
    }
  });
  return { total: Math.round(total * 100) / 100, itensNoTotal };
};

export const progressoDoLote = (lote: Lote) => {
  const pendentes = lote.itens.filter((i) => i.status === 'pendente').length;
  const salvos = lote.itens.filter((i) => i.status === 'salvo').length;
  return {
    total: lote.itens.length,
    pendentes,
    pesquisados: lote.itens.length - pendentes,
    salvos,
  };
};

export const situacaoDoItem = (item: ItemLote): string => {
  if (item.status === 'pendente') return 'pendente';
  if (!item.resumo || item.resumo.amostras === 0) return 'sem amostras';
  if (item.resumo.valor === null) return 'sem preço unitário de referência';
  if (item.resumo.refinar) return 'pesquisado — vale refinar a descrição';
  return item.status === 'salvo' ? 'pesquisa salva' : 'pesquisado';
};

const atualizarItem = (lote: Lote, id: string, mudar: (item: ItemLote) => ItemLote): Lote => ({
  ...lote,
  itens: lote.itens.map((item) => (item.id === id ? mudar(item) : item)),
});

export const marcarPesquisado = (lote: Lote, id: string, resumo: ResumoItemLote): Lote =>
  atualizarItem(lote, id, (item) => ({
    ...item,
    resumo,
    status: item.status === 'salvo' ? 'salvo' : 'pesquisado',
  }));

export const marcarSalvo = (lote: Lote, id: string, pesquisaSalvaId: number): Lote =>
  atualizarItem(lote, id, (item) => ({ ...item, status: 'salvo', pesquisaSalvaId }));

export const removerItem = (lote: Lote, id: string): Lote | null => {
  const itens = lote.itens.filter((item) => item.id !== id);
  return itens.length ? { ...lote, itens } : null;
};

export const lerQuantidade = (texto: string): number | null => {
  const limpo = texto.replace(/\s/g, '');
  if (!limpo) return null;
  let normal = limpo;
  if (limpo.indexOf(',') >= 0) normal = limpo.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) normal = limpo.replace(/\./g, '');
  if (!/^\d+(\.\d+)?$/.test(normal)) return null;
  const valor = Number(normal);
  return Number.isFinite(valor) && valor > 0 ? valor : null;
};

const fmtQuantidade = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });

export const formatarQuantidade = (valor: number | null): string =>
  valor === null ? '' : fmtQuantidade.format(valor);

const celulaCsv = (v: string) => (/[";\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
const numeroCsv = (n: number | null | undefined) =>
  n === null || n === undefined ? '' : String(n).replace('.', ',');

export const exportarLoteCsv = (lote: Lote): void => {
  const cabecalho = [
    'item',
    'lote',
    'descricao',
    'quantidade',
    'unidade',
    'preco_referencia',
    'unidade_do_preco',
    'amostras',
    'total_estimado',
    'valor_referencia_do_arquivo',
    'catalogo',
    'situacao',
  ];
  const linhas = lote.itens.map((item, i) =>
    [
      item.numero || String(i + 1),
      item.lote || '',
      item.descricao,
      numeroCsv(item.quantidade),
      item.unidade || '',
      numeroCsv(item.resumo ? item.resumo.valor : null),
      (item.resumo && item.resumo.unidade) || '',
      item.resumo ? String(item.resumo.amostras) : '',
      numeroCsv(totalEstimado(item)),
      numeroCsv(item.valorReferencia),
      item.codigoCatalogo || '',
      situacaoDoItem(item),
    ]
      .map(celulaCsv)
      .join(';'),
  );
  const { total, itensNoTotal } = totaisDoLote(lote);
  const rodape = [
    '',
    '',
    `TOTAL ESTIMADO (${itensNoTotal} de ${lote.itens.length} itens)`,
    '',
    '',
    '',
    '',
    '',
    numeroCsv(total),
    '',
    '',
    '',
  ]
    .map(celulaCsv)
    .join(';');
  const nome = nomeDoLote(lote).replace(/\s+/g, '-').toLowerCase() || 'itens';
  baixarArquivo(
    `\uFEFF${[cabecalho.join(';')].concat(linhas, [rodape]).join('\n')}`,
    `analise-mercado-${nome}.csv`,
    'text/csv;charset=utf-8',
  );
};

const texto = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const positivo = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;

const lerItem = (bruto: unknown): ItemLote | null => {
  if (!bruto || typeof bruto !== 'object') return null;
  const b = bruto as Record<string, unknown>;
  const id = texto(b.id);
  const descricao = texto(b.descricao);
  if (!id || !descricao) return null;
  const pesquisaSalvaId = positivo(b.pesquisaSalvaId);
  const status: StatusItemLote =
    b.status === 'pesquisado' || (b.status === 'salvo' && pesquisaSalvaId)
      ? (b.status as StatusItemLote)
      : b.status === 'salvo'
      ? 'pesquisado'
      : 'pendente';
  const r = b.resumo && typeof b.resumo === 'object' ? (b.resumo as Record<string, unknown>) : null;
  return {
    id,
    numero: texto(b.numero),
    lote: texto(b.lote),
    descricao: descricao.slice(0, DESCRICAO_MAX),
    quantidade: positivo(b.quantidade),
    unidade: texto(b.unidade),
    codigoCatalogo: texto(b.codigoCatalogo),
    valorReferencia: positivo(b.valorReferencia),
    status,
    resumo: r
      ? {
          valor: positivo(r.valor),
          unidade: texto(r.unidade),
          amostras: Math.max(0, typeof r.amostras === 'number' ? r.amostras : 0),
          refinar: texto(r.refinar),
        }
      : null,
    pesquisaSalvaId,
  };
};

export const chaveDoLote = (userId: number) => `${STORAGE_PREFIXO}${userId}`;

export const carregarLote = (userId: number): Lote | null => {
  try {
    const bruto = JSON.parse(window.localStorage.getItem(chaveDoLote(userId)) || 'null');
    if (!bruto || typeof bruto !== 'object' || !Array.isArray(bruto.itens)) return null;
    const vistos: Record<string, boolean> = {};
    const itens = (bruto.itens as unknown[])
      .map(lerItem)
      .filter((item): item is ItemLote => {
        if (!item || vistos[item.id]) return false;
        vistos[item.id] = true;
        return true;
      })
      .slice(0, LOTE_MAX_ITENS);
    if (!itens.length) return null;
    return {
      arquivo: texto(bruto.arquivo) || 'itens',
      importadoEm: texto(bruto.importadoEm) || new Date().toISOString(),
      itens,
    };
  } catch (e) {
    return null;
  }
};

export const salvarLote = (userId: number, lote: Lote | null): void => {
  try {
    if (lote) window.localStorage.setItem(chaveDoLote(userId), JSON.stringify(lote));
    else window.localStorage.removeItem(chaveDoLote(userId));
  } catch (e) {
    // sem persistência: o lote segue em memória
  }
};

const abrirBanco = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const pedido = indexedDB.open(BANCO, 1);
      pedido.onupgradeneeded = () => {
        if (!pedido.result.objectStoreNames.contains(LOJA)) pedido.result.createObjectStore(LOJA);
      };
      pedido.onsuccess = () => resolve(pedido.result);
      pedido.onerror = () => resolve(null);
      pedido.onblocked = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });

const comLoja = async <T>(
  modo: IDBTransactionMode,
  operar: (loja: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | null> => {
  const db = await abrirBanco();
  if (!db) return null;
  try {
    return await new Promise<T | null>((resolve) => {
      const tx = db.transaction(LOJA, modo);
      const pedido = operar(tx.objectStore(LOJA));
      tx.oncomplete = () => resolve(pedido && pedido.result !== undefined ? pedido.result : null);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    });
  } catch (e) {
    return null;
  } finally {
    db.close();
  }
};

const chaveDoResultado = (userId: number, itemId: string) => `${userId}:${itemId}`;

export const guardarResultado = async (
  userId: number,
  itemId: string,
  guardado: ResultadoGuardado,
): Promise<void> => {
  await comLoja('readwrite', (loja) => loja.put(guardado, chaveDoResultado(userId, itemId)));
};

export const lerResultado = async (
  userId: number,
  itemId: string,
): Promise<ResultadoGuardado | null> => {
  const lido = await comLoja<ResultadoGuardado | undefined>('readonly', (loja) =>
    loja.get(chaveDoResultado(userId, itemId)),
  );
  return lido &&
    typeof lido === 'object' &&
    lido.resultado &&
    Array.isArray(lido.resultado.hits) &&
    lido.busca &&
    lido.busca.query
    ? lido
    : null;
};

export const manterSoResultadosDe = async (userId: number, itemIds: string[]): Promise<void> => {
  const validos: Record<string, boolean> = {};
  itemIds.forEach((id) => {
    validos[chaveDoResultado(userId, id)] = true;
  });
  const prefixo = `${userId}:`;
  await comLoja('readwrite', (loja) => {
    const cursor = loja.openKeyCursor();
    cursor.onsuccess = () => {
      const atual = cursor.result;
      if (!atual) return;
      const k = String(atual.key);
      if (k.indexOf(prefixo) === 0 && !validos[k]) loja.delete(atual.key);
      atual.continue();
    };
  });
};
